// src/components/pages/message/timecapsule/Step1Compose.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Card,
  CardMedia,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import IconNewMedia from "../../../../assets/icons/icon-new-media.svg";
import IconMyGallery from "../../../../assets/icons/icon-my-gallery.svg";
import { ReactComponent as IconTrash } from "../../../../assets/icons/icon-delete.svg";
import ActionButton from "../../../atoms/ActionButton";
import VideoRecorderModal from "./VideoRecorderModal";
import PhotoCaptureModal from "./PhotoCaptureModal";

const Step1Compose = ({ wizardData, setWizardData, onNext }) => {
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const MAX_CHAR = 280;
  const navigate = useNavigate();

  // Detect if the user is on an iOS device.
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    const handleSubjectChange = (e) => {
      const newValue = e.target.value;
      if (newValue.length > MAX_CHAR) {
        setError(`Subject cannot exceed ${MAX_CHAR} characters.`);
      } else {
        setError("");
        setWizardData({ ...wizardData, subject: newValue });
      }
    };

  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length > MAX_CHAR) {
      setError(`Message cannot exceed ${MAX_CHAR} characters.`);
    } else {
      setError("");
      setWizardData({ ...wizardData, message: newValue });
    }
  };

  const handleMediaUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setWizardData({
        ...wizardData,
        mediaFile: {
          file,
          url,
          type: file.type,
        },
      });
    }
  };

  const handleRemoveMedia = () => {
    setWizardData({ ...wizardData, mediaFile: null });
  };

  // Control modal visibility for photo and video capture.
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);

  const handleNextStep = () => {
    if (!wizardData.message.trim()) {
      // Instead of using alert, show a Snackbar with a filled Alert.
      setSnackbarMessage("Message is required.");
      setSnackbarOpen(true);
      return;
    }
    onNext();
  };

  const handleVideoCapture = (mediaBlobUrl) => {
    setWizardData({
      ...wizardData,
      mediaFile: {
        url: mediaBlobUrl,
        type: "video/mp4",
      },
    });
    setShowVideoRecorder(false);
  };

  const handlePhotoCapture = (imageSrc) => {
    setWizardData({
      ...wizardData,
      mediaFile: {
        url: imageSrc,
        type: "image/jpeg",
      },
    });
    setShowPhotoCapture(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Step 1: Compose
      </Typography>
      <TextField
        label="Subject"
        fullWidth
        value={wizardData.subject}
        onChange={handleSubjectChange}
        error={Boolean(error)}
        helperText={error || `${wizardData.subject.length}/${MAX_CHAR/2}`}
      />
      <TextField
        label="Your Message"
        multiline
        minRows={4}
        fullWidth
        value={wizardData.message}
        onChange={handleMessageChange}
        error={Boolean(error)}
        helperText={error || `${wizardData.message.length}/${MAX_CHAR}`}
      />
      {wizardData.mediaFile && (
        <Card sx={{ mb: 2, position: "relative" }} elevation={0}>
          {wizardData.mediaFile.type.startsWith("image/") ? (
            <CardMedia
              component="img"
              image={wizardData.mediaFile.url}
              alt="preview"
              sx={{ width: "100%", height: "auto", borderRadius: "20px" }}
            />
          ) : wizardData.mediaFile.type.startsWith("video/") ? (
            <CardMedia
              component="video"
              src={wizardData.mediaFile.url}
              controls
              sx={{ width: "100%", height: "auto", borderRadius: "20px" }}
            />
          ) : null}
          {/* Absolutely position the delete button */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
            }}
          >
            <IconButton
              onClick={handleRemoveMedia}
              sx={(theme) => ({
                width: 50,
                height: 50,
                borderRadius: "50%",
                backgroundColor: theme.colors.red,
                color: theme.colors.white,
                "&:hover": {
                  backgroundColor: theme.colors.darkBlue,
                },
              })}
            >
              <IconTrash width={24} height={24} />
            </IconButton>
          </Box>
        </Card>
      )}

      {/* Button container for media actions */}
      {!wizardData.mediaFile && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mb: 3,
            mt: 3,
          }}
        >
          {isIOS ? (
            <ActionButton
              icon={IconMyGallery}
              text="Add Media"
              inputProps={{
                type: "file",
                accept: "image/*,video/*",
                onChange: handleMediaUpload,
              }}
              sx={(theme) => ({
                backgroundColor: theme.colors.white,
                color: theme.colors.blue,
                border: `1px solid ${theme.colors.blue}`,
                borderRadius: "50px",
                padding: "10px 20px",
                marginBottom: "0px",
                fontSize: "1rem",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              })}
            />
          ) : (
            <>
              <ActionButton
                icon={IconNewMedia}
                text="New Photo"
                onClick={() => setShowPhotoCapture(true)}
                sx={(theme) => ({
                  backgroundColor: theme.colors.white,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  borderRadius: "50px",
                  padding: "10px 20px",
                  marginBottom: "0px",
                  fontSize: "1rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                })}
              />

              <ActionButton
                icon={IconNewMedia}
                text="New Video"
                onClick={() => setShowVideoRecorder(true)}
                sx={(theme) => ({
                  backgroundColor: theme.colors.white,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  borderRadius: "50px",
                  padding: "10px 20px",
                  marginBottom: "0px",
                  fontSize: "1rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                })}
              />

              <ActionButton
                icon={IconMyGallery}
                text="My Gallery"
                inputProps={{
                  type: "file",
                  accept: "image/*,video/*",
                  onChange: handleMediaUpload,
                }}
                sx={(theme) => ({
                  backgroundColor: theme.colors.white,
                  color: theme.colors.blue,
                  border: `1px solid ${theme.colors.blue}`,
                  borderRadius: "50px",
                  padding: "10px 20px",
                  marginBottom: "0px",
                  fontSize: "1rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                })}
              />
            </>
          )}
        </Box>
      )}

      <PhotoCaptureModal
        open={showPhotoCapture}
        onClose={() => setShowPhotoCapture(false)}
        onCapture={handlePhotoCapture}
      />
      <VideoRecorderModal
        open={showVideoRecorder}
        onClose={() => setShowVideoRecorder(false)}
        onCapture={handleVideoCapture}
      />

      {/* Navigation Buttons */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 10,
        }}
      >
        <ActionButton
          text={"Cancel"}
          onClick={() => navigate("/messages/drop")}
          sx={(theme) => ({
            backgroundColor: theme.colors.white,
            color: "black",
            textDecoration: "underline",
            borderRadius: "50px",
            padding: "14px 0px",
            marginBottom: "10px",
            fontWeight: "bold",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          })}
        />
        <ActionButton
          icon={IconNextArrow}
          iconWidth={15}
          iconHeight={15}
          text={"Next Step"}
          iconPosition="right"
          onClick={handleNextStep}
          sx={(theme) => ({
            backgroundColor: theme.colors.blue,
            color: theme.colors.white,
            borderRadius: "50px",
            padding: "14px 30px",
            marginBottom: "10px",
            fontWeight: "bold",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          })}
        />
      </Box>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Step1Compose;
